import React, { useState, useEffect } from 'react';
import { addStellungnahme, getCustomersForDropdown } from '../../api';

function StellungnahmeForm({ user, onCancel, onStellungnahmeAdded }) {
  const [formData, setFormData] = useState({
    leadId: '',
    nachricht: '',
    anhang: '',
    status: 'Offen'
  });
  
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Kunden für Dropdown laden
  useEffect(() => {
    const loadCustomers = async () => {
      setCustomersLoading(true);
      setError('');
      
      try {
        const data = await getCustomersForDropdown(user.id);
        
        if (data.success) {
          setCustomers(data.customers);
        } else {
          setError(data.error || 'Fehler beim Laden der Kunden');
        }
      } catch (err) {
        setError('Netzwerkfehler: ' + err.message);
      } finally {
        setCustomersLoading(false);
      }
    };

    loadCustomers();
  }, [user.id]);

  // Handler für Feld-Änderungen
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Anhang-Handler
  const handleAnhangChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In einer echten Anwendung würden Sie hier den File Upload handhaben
      // Für jetzt speichern wir nur den Dateinamen
      setFormData({
        ...formData,
        anhang: file.name
      });
    }
  };

  // Formular absenden
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validierung
    if (!formData.leadId) {
      setError('Bitte wählen Sie einen Kunden aus.');
      return;
    }
    
    if (!formData.nachricht.trim()) {
      setError('Bitte geben Sie eine Nachricht ein.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Daten für die API vorbereiten
      const stellungnahmeData = {
        userId: user.id,
        leadId: formData.leadId,
        nachricht: formData.nachricht.trim(),
        anhang: formData.anhang || null,
        status: 'Offen'
      };
      
      const data = await addStellungnahme(stellungnahmeData);
      
      if (data.success) {
        setSuccess('Stellungnahme erfolgreich erstellt!');
        
        // Nach 2 Sekunden zur Liste zurückkehren
        setTimeout(() => {
          onStellungnahmeAdded();
        }, 2000);
      } else {
        setError(data.error || 'Fehler beim Erstellen der Stellungnahme.');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lead-form">
      <div className="leads-header">
        <h2>Neue Stellungnahme erstellen</h2>
        <div className="leads-actions">
          <button className="back-button" onClick={onCancel}>
            ← Zurück zur Übersicht
          </button>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="create-user-form-container">
        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="form-section">
            <h3>Stellungnahme Details</h3>
            
            <div className="form-row">
              <div className="form-field">
                <label>Kunde*</label>
                <select
                  name="leadId"
                  value={formData.leadId}
                  onChange={handleChange}
                  required
                  disabled={customersLoading}
                >
                  <option value="">-- Kunde auswählen --</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                      {customer.email && ` (${customer.email})`}
                    </option>
                  ))}
                </select>
                {customersLoading && (
                  <div className="loading-indicator">Lade Kunden...</div>
                )}
                {customers.length === 0 && !customersLoading && (
                  <div className="helper-text">
                    Keine Kunden verfügbar. Erstellen Sie zuerst einen Kunden.
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label>Nachricht*</label>
                <textarea
                  name="nachricht"
                  value={formData.nachricht}
                  onChange={handleChange}
                  placeholder="Beschreiben Sie hier die Stellungnahme..."
                  rows={6}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label>Anhang (optional)</label>
                <input
                  type="file"
                  name="anhang"
                  onChange={handleAnhangChange}
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <div className="helper-text">
                  Erlaubte Dateitypen: PDF, DOC, DOCX, TXT, JPG, PNG
                </div>
                {formData.anhang && (
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '8px', 
                    backgroundColor: '#f0f9ff', 
                    border: '1px solid #0ea5e9', 
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    📎 Ausgewählte Datei: {formData.anhang}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onCancel}
              disabled={loading}
            >
              Abbrechen
            </button>
            <button 
              type="submit" 
              disabled={loading || !formData.leadId || !formData.nachricht.trim()}
            >
              {loading ? 'Wird erstellt...' : 'Stellungnahme erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StellungnahmeForm;